import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from './leafletDrawAreaFix';

export interface MarkerHint {
  position?: [number, number] | null;
}

export interface PolygonHint {
  positions?: Array<[number, number]> | null;
}

interface FitBoundsToDataProps {
  /** Marker hints — anything with a `[lat, lng]` `position` array. */
  markers?: MarkerHint[];
  /** Polygon hints — anything with a `positions` array of `[lat, lng]`. */
  polygons?: PolygonHint[];
  /** Skip the fit. Convenient escape hatch when the caller wants to keep
   *  the user's current pan/zoom (e.g. WildfireDetail). */
  enabled?: boolean;
  /** Inner padding (px) when fitting; defaults to 50 on each side. */
  padding?: [number, number];
  /** Hard cap on the zoom level the fit may select. Defaults to 16. */
  maxZoom?: number;
}

/**
 * Once-per-data-change `map.fitBounds()` to encompass every marker + polygon
 * vertex passed in. The legacy MapComponent ran this unconditionally on
 * every render — that's intentionally OPT-IN here (`enabled` default false
 * in the unified MapCanvas) because auto-recentering on every data change
 * caused jumpy behaviour on pages like WildfireDetail.
 */
export function FitBoundsToData({
  markers,
  polygons,
  enabled = true,
  padding = [50, 50],
  maxZoom = 16,
}: FitBoundsToDataProps) {
  const map = useMap();

  useEffect(() => {
    if (!enabled || !map) return;

    try {
      const bounds = L.latLngBounds([]);
      let hasBounds = false;

      markers?.forEach((m) => {
        const p = m?.position;
        if (
          Array.isArray(p) &&
          p.length >= 2 &&
          typeof p[0] === 'number' &&
          typeof p[1] === 'number'
        ) {
          bounds.extend(L.latLng(p[0], p[1]));
          hasBounds = true;
        }
      });

      polygons?.forEach((poly) => {
        poly?.positions?.forEach((p) => {
          if (
            Array.isArray(p) &&
            p.length >= 2 &&
            typeof p[0] === 'number' &&
            typeof p[1] === 'number'
          ) {
            bounds.extend(L.latLng(p[0], p[1]));
            hasBounds = true;
          }
        });
      });

      if (hasBounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding, maxZoom });
      }
    } catch (err) {
      console.error('FitBoundsToData: failed to fit bounds', err);
    }
  }, [map, markers, polygons, enabled, padding, maxZoom]);

  return null;
}

export default FitBoundsToData;
